import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ordersAPI, formSchemaAPI, customersAPI, settingsAPI, invoicesAPI } from '../../lib/api';
import { SocialLinkButton, SOCIAL_NETWORKS, SocialIcon } from '../Customers/CustomersPage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, Eye, Edit, Trash2, ArrowLeft, 
  ShoppingCart, Calendar as CalendarIcon, X, ChevronDown, Images, ImagePlus,
  Check, ChevronsUpDown, Phone, Mail, MapPin, Banknote, CreditCard, FileText, User, Wallet, Copy, Bell,
  Paperclip, Download, FileUp, ArrowUpDown, Receipt, ExternalLink,
  Package, Star, Heart, Tag, Bookmark, Flag, Truck, Gift,
  Scissors, Palette, Cake, Coffee, Home, Building, Briefcase,
  Shield, Zap, Music, Camera, Globe, Key, Award, Crown, Diamond,
  Flame, Leaf, Sun, Moon, Cloud, Umbrella, Anchor, Compass, Clock, Sparkles
} from 'lucide-react';

// Section icon map (must match FormBuilderPage)
const SECTION_ICONS_MAP = {
  'package': Package, 'shopping-cart': ShoppingCart, 'user': User, 'star': Star,
  'heart': Heart, 'map-pin': MapPin, 'phone': Phone, 'mail': Mail,
  'file-text': FileText, 'tag': Tag, 'bookmark': Bookmark, 'flag': Flag,
  'bell': Bell, 'truck': Truck, 'gift': Gift, 'credit-card': CreditCard,
  'scissors': Scissors, 'palette': Palette, 'cake': Cake, 'coffee': Coffee,
  'home': Home, 'building': Building, 'briefcase': Briefcase, 'shield': Shield,
  'zap': Zap, 'music': Music, 'camera': Camera, 'globe': Globe,
  'key': Key, 'award': Award, 'crown': Crown, 'diamond': Diamond,
  'flame': Flame, 'leaf': Leaf, 'sun': Sun, 'moon': Moon,
  'cloud': Cloud, 'umbrella': Umbrella, 'anchor': Anchor, 'compass': Compass,
  'calendar': CalendarIcon, 'clock': Clock, 'sparkles': Sparkles,
};
import { formatDate, formatDateTime, formatCurrency, formatFormDate, ORDER_STATUSES, cn } from '../../lib/utils';
import { filesAPI } from '../../lib/api';
import PaywallDialog from '../../components/PaywallDialog';
import { CreateInvoiceDialog } from '../Invoices/InvoicesPage';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

// Social icons component for order cards
function CustomerSocialIcons({ customer }) {
  if (!customer) return null;
  const socialLinks = customer.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v);
  if (!hasSocials) return null;

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {socialLinks.telegram && (
        <a 
          href={`https://t.me/${socialLinks.telegram.replace('@', '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#0088cc]/10 text-[#0088cc] transition-colors"
          title="Telegram"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        </a>
      )}
      {socialLinks.whatsapp && (
        <a 
          href={`https://wa.me/${socialLinks.whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#25D366]/10 text-[#25D366] transition-colors"
          title="WhatsApp"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}
      {socialLinks.instagram && (
        <a 
          href={`https://instagram.com/${socialLinks.instagram.replace('@', '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#E4405F]/10 text-[#E4405F] transition-colors"
          title="Instagram"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
          </svg>
        </a>
      )}
      {socialLinks.vk && (
        <a 
          href={`https://vk.com/${socialLinks.vk}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#0077FF]/10 text-[#0077FF] transition-colors"
          title="ВКонтакте"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.12-5.339-3.202-2.17-3.042-2.763-5.32-2.763-5.785 0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.27-1.422 2.18-3.61 2.18-3.61.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
          </svg>
        </a>
      )}
      {socialLinks.ok && (
        <a 
          href={`https://ok.ru/${socialLinks.ok}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#EE8208]/10 text-[#EE8208] transition-colors"
          title="Одноклассники"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.8c1.99 0 3.6 1.61 3.6 3.6s-1.61 3.6-3.6 3.6-3.6-1.61-3.6-3.6 1.61-3.6 3.6-3.6zm0 1.8c-.994 0-1.8.806-1.8 1.8s.806 1.8 1.8 1.8 1.8-.806 1.8-1.8-.806-1.8-1.8-1.8zm4.05 8.4c-.396.396-.99.504-1.5.27-.78-.348-1.668-.528-2.55-.528s-1.77.18-2.55.528c-.51.234-1.104.126-1.5-.27-.396-.396-.504-.99-.27-1.5.348-.78 1.02-1.44 1.89-1.89-.87-.45-1.542-1.11-1.89-1.89-.234-.51-.126-1.104.27-1.5.396-.396.99-.504 1.5-.27.78.348 1.668.528 2.55.528s1.77-.18 2.55-.528c.51-.234 1.104-.126 1.5.27.396.396.504.99.27 1.5-.348.78-1.02 1.44-1.89 1.89.87.45 1.542 1.11 1.89 1.89.234.51.126 1.104-.27 1.5zm-1.05 2.7l2.4 2.4c.396.396.396 1.038 0 1.434-.396.396-1.038.396-1.434 0L12 17.7l-3.966 3.834c-.396.396-1.038.396-1.434 0-.396-.396-.396-1.038 0-1.434l2.4-2.4"/>
          </svg>
        </a>
      )}
      {socialLinks.vkmax && (
        <a 
          href={`https://max.vk.com/${socialLinks.vkmax}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#7B68EE]/10 text-[#7B68EE] transition-colors"
          title="VK Max"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z"/>
          </svg>
        </a>
      )}
      {socialLinks.facebook && (
        <a 
          href={`https://facebook.com/${socialLinks.facebook}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#1877F2]/10 text-[#1877F2] transition-colors"
          title="Facebook"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </a>
      )}
    </div>
  );
}

// Orders List Page
export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const PAGE_SIZE = 15;
  const loadMoreRef = useRef(null);
  const skipRef = useRef(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [dateFilterType, setDateFilterType] = useState('all'); // all, created, calendar
  const [filterYear, setFilterYear] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [formSchemas, setFormSchemas] = useState({});
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallResource, setPaywallResource] = useState('orders');
  const [unpaidDialogOpen, setUnpaidDialogOpen] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { orderId, newStatus }

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  // Handle view parameter to open order details sheet
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && !loading && orders.length > 0) {
      const order = orders.find(o => o.id === viewId);
      if (order) {
        setViewingOrder(order);
      }
      // Clear the view param from URL
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  }, [orders, loading, searchParams, setSearchParams]);

  // Handle create parameter to open order creation wizard
  useEffect(() => {
    const shouldCreate = searchParams.get('create');
    if (shouldCreate === 'true' && !loading) {
      setCreatingOrder(true);
      // Clear the create param from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  const loadOrders = async () => {
    try {
      skipRef.current = 0;
      const params = { skip: 0, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [ordersRes, customersRes, schemasRes] = await Promise.all([
        ordersAPI.getAll(params),
        customersAPI.getAll({ limit: 1000 }),
        formSchemaAPI.getAll()
      ]);
      const total = parseInt(ordersRes.headers?.['x-total-count'] || '0', 10);
      setTotalOrders(total);
      setOrders(ordersRes.data);
      skipRef.current = ordersRes.data.length;
      setHasMore(ordersRes.data.length < total);
      // Create a map of customers by ID for quick lookup
      const customersMap = {};
      customersRes.data.forEach(c => { customersMap[c.id] = c; });
      setCustomers(customersMap);
      // Create a map of form schemas by ID
      const schemasMap = {};
      schemasRes.data.forEach(s => { schemasMap[s.id] = s; });
      setFormSchemas(schemasMap);
    } catch (error) {
      toast.error('Ошибка загрузки заказов');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreOrders = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = { skip: skipRef.current, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await ordersAPI.getAll(params);
      const total = parseInt(res.headers?.['x-total-count'] || '0', 10);
      setTotalOrders(total);
      setOrders(prev => {
        const existingIds = new Set(prev.map(o => o.id));
        const newOrders = res.data.filter(o => !existingIds.has(o.id));
        return [...prev, ...newOrders];
      });
      skipRef.current += res.data.length;
      setHasMore(skipRef.current < total);
    } catch (error) {
      console.error('Error loading more orders:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, statusFilter]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreOrders();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMoreOrders]);

  // Get the calendar date value from order's form data
  const getCalendarDate = (order) => {
    if (!order?.form_schema_id || !order?.data) return null;
    const schema = formSchemas[order.form_schema_id];
    if (!schema?.fields) return null;
    const calField = schema.fields.find(f => f.type === 'date' && f.use_for_calendar);
    if (!calField) return null;
    const value = order.data[calField.id];
    if (!value) return null;
    return { label: calField.label, date: formatFormDate(value) };
  };

  const deleteOrder = async (id) => {
    const order = orders.find(o => o.id === id);
    const orderDisplay = order?.order_number_display || `№${order?.order_number || id}`;
    if (!window.confirm(`Удалить заказ ${orderDisplay}?\n\nЭто действие нельзя отменить.`)) return;
    try {
      await ordersAPI.delete(id);
      toast.success('Заказ удалён');
      loadOrders();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const duplicateOrder = async (order) => {
    try {
      await ordersAPI.create({
        form_schema_id: order.form_schema_id || null,
        customer_id: order.customer_id || null,
        status: 'new',
        total: order.total || 0,
        prepayment: 0,
        data: order.data || {},
      });
      toast.success(`Заказ ${order.order_number_display || '№' + order.order_number} скопирован`);
      loadOrders();
    } catch (error) {
      toast.error('Ошибка при копировании заказа');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    // If changing to completed or cancelled, check for unpaid invoices first
    if (newStatus === 'completed' || newStatus === 'cancelled') {
      try {
        const res = await invoicesAPI.getAll({ order_id: orderId });
        const invoices = res.data || [];
        const unpaid = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
        if (unpaid.length > 0) {
          setUnpaidInvoices(unpaid);
          setPendingStatusChange({ orderId, newStatus });
          setUnpaidDialogOpen(true);
          return; // Don't change status yet — wait for dialog
        }
      } catch (e) {
        // If error checking invoices, proceed with status change
      }
    }
    await executeStatusChange(orderId, newStatus);
  };

  const executeStatusChange = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) return;
      await ordersAPI.update(orderId, {
        data: order.data,
        status: newStatus,
        prepayment: order.prepayment,
        total: order.total,
      });
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: newStatus } : o
      ));
      toast.success('Статус обновлён');
    } catch (error) {
      toast.error('Ошибка обновления статуса');
    }
  };

  const handleUnpaidDialogMarkPaid = async () => {
    if (!pendingStatusChange) return;
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchPay(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) отмечены как оплаченные`);
    } catch (e) {
      toast.error('Ошибка при обновлении счетов');
    }
    await executeStatusChange(pendingStatusChange.orderId, pendingStatusChange.newStatus);
    if (viewingOrder && viewingOrder.id === pendingStatusChange.orderId) {
      setViewingOrder(prev => prev ? { ...prev, status: pendingStatusChange.newStatus } : prev);
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    setPendingStatusChange(null);
  };

  const handleUnpaidDialogDelete = async () => {
    if (!pendingStatusChange) return;
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchDelete(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) удалены`);
    } catch (e) {
      toast.error('Ошибка при удалении счетов');
    }
    await executeStatusChange(pendingStatusChange.orderId, pendingStatusChange.newStatus);
    if (viewingOrder && viewingOrder.id === pendingStatusChange.orderId) {
      setViewingOrder(prev => prev ? { ...prev, status: pendingStatusChange.newStatus } : prev);
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    setPendingStatusChange(null);
  };

  const handleUnpaidDialogClose = () => {
    // Close without changing anything
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    setPendingStatusChange(null);
  };

  // Get raw calendar date for an order
  const getCalendarDateRaw = (order) => {
    const schema = formSchemas[order.form_schema_id];
    if (!schema || !schema.fields) return null;
    const calField = schema.fields.find(f => f.type === 'date' && f.use_for_calendar);
    if (!calField) return null;
    const val = order.data && order.data[calField.id];
    return val || null;
  };

  const MONTHS = [
    { value: '1', label: 'Январь' }, { value: '2', label: 'Февраль' },
    { value: '3', label: 'Март' }, { value: '4', label: 'Апрель' },
    { value: '5', label: 'Май' }, { value: '6', label: 'Июнь' },
    { value: '7', label: 'Июль' }, { value: '8', label: 'Август' },
    { value: '9', label: 'Сентябрь' }, { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' }, { value: '12', label: 'Декабрь' },
  ];

  // Collect available years from orders
  const availableYears = [...new Set(orders.map(o => {
    const d = new Date(o.created_at);
    return d.getFullYear();
  }).concat(orders.map(o => {
    const raw = getCalendarDateRaw(o);
    return raw ? new Date(raw).getFullYear() : null;
  }).filter(Boolean)))].sort((a, b) => b - a);

  const filteredOrders = orders.filter(order => {
    // Text search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const customerName = order.customer_id && customers[order.customer_id]
        ? customers[order.customer_id].name.toLowerCase()
        : '';
      const orderNumber = String(order.order_number || '');
      const orderNumberDisplay = String(order.order_number_display || '').toLowerCase();
      if (!orderNumber.includes(query) && !orderNumberDisplay.includes(query) && !order.id.toLowerCase().includes(query) && !customerName.includes(query)) {
        return false;
      }
    }

    // Date filters
    const hasDateFilter = filterYear !== 'all' || filterMonth !== 'all' || filterDateFrom || filterDateTo;
    if (!hasDateFilter) return true;

    // Determine which date to use for filtering
    let dateStr;
    if (dateFilterType === 'calendar') {
      dateStr = getCalendarDateRaw(order);
      if (!dateStr) return false; // No calendar date — exclude
    } else {
      dateStr = order.created_at;
    }

    if (!dateStr) return false;
    const date = new Date(dateStr);

    // Year filter
    if (filterYear !== 'all' && date.getFullYear() !== parseInt(filterYear)) {
      return false;
    }

    // Month filter
    if (filterMonth !== 'all' && (date.getMonth() + 1) !== parseInt(filterMonth)) {
      return false;
    }

    // Date range filter
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      if (date < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      if (date > to) return false;
    }

    return true;
  });

  // Sort orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    switch (sortBy) {
      case 'created_asc':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'created_desc':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'calendar_asc': {
        const da = getCalendarDateRaw(a);
        const db = getCalendarDateRaw(b);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return new Date(da) - new Date(db);
      }
      case 'calendar_desc': {
        const da = getCalendarDateRaw(a);
        const db = getCalendarDateRaw(b);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return new Date(db) - new Date(da);
      }
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Заказы</h1>
          <p className="text-sm text-muted-foreground">Управление заказами</p>
        </div>
        <Button onClick={() => setCreatingOrder(true)} data-testid="create-order-btn">
          <Plus className="h-4 w-4 mr-2" />
          Новый заказ
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по ID или клиенту..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-orders-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40 shrink-0" data-testid="status-filter">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-52 shrink-0" data-testid="sort-orders">
                <ArrowUpDown className="h-4 w-4 mr-2 shrink-0" />
                <SelectValue placeholder="Сортировка" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_desc">Сначала новые</SelectItem>
                <SelectItem value="created_asc">Сначала старые</SelectItem>
                <SelectItem value="calendar_desc">По событию ↓</SelectItem>
                <SelectItem value="calendar_asc">По событию ↑</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilterType} onValueChange={(v) => { setDateFilterType(v); }}>
              <SelectTrigger className="w-full md:w-44 shrink-0">
                <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все даты</SelectItem>
                <SelectItem value="created">По дате создания</SelectItem>
                <SelectItem value="calendar">По дате события</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Expanded date filters (shown only when date filter is active) */}
          {dateFilterType !== 'all' && (
            <div className="flex flex-col sm:flex-row gap-3 items-end flex-wrap">
              <div className="flex gap-1.5">
                {[
                  { label: 'Вчера', offset: -1 },
                  { label: 'Сегодня', offset: 0 },
                  { label: 'Завтра', offset: 1 },
                ].map(({ label, offset }) => {
                  const d = new Date();
                  d.setDate(d.getDate() + offset);
                  const iso = d.toISOString().slice(0, 10);
                  const isActive = filterDateFrom === iso && filterDateTo === iso;
                  return (
                    <Button
                      key={label}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        if (isActive) {
                          setFilterDateFrom('');
                          setFilterDateTo('');
                        } else {
                          setFilterDateFrom(iso);
                          setFilterDateTo(iso);
                          setFilterYear('all');
                          setFilterMonth('all');
                        }
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Год" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все годы</SelectItem>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Месяц" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все месяцы</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="От"
                />
                <span className="text-muted-foreground text-sm">—</span>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="До"
                />
              </div>
              {(filterYear !== 'all' || filterMonth !== 'all' || filterDateFrom || filterDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterYear('all');
                    setFilterMonth('all');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }}
                  className="shrink-0"
                >
                  <X className="h-4 w-4 mr-1" />
                  Сбросить
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orders List */}
      <div className="md:hidden">
        {loading ? (
          <Card>
            <CardContent className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </CardContent>
          </Card>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Нет заказов</p>
              <p className="text-muted-foreground mb-1">Создайте первый заказ</p>
              <p className="text-xs text-muted-foreground/60 mb-4">YaDela.ru — твои дела проще</p>
              <Button onClick={() => setCreatingOrder(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Создать заказ
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {sortedOrders.map((order) => (
              <Card
                key={order.id}
                className="hover:shadow-md transition-shadow"
                data-testid={`order-row-${order.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-primary">{order.order_number_display || `№${order.order_number}`}</span>
                      {order.form_schema_name && (
                        <Badge variant="outline" className="text-xs">
                          {order.form_schema_name}
                        </Badge>
                      )}
                    </div>
                    <Select 
                      value={order.status} 
                      onValueChange={(value) => updateOrderStatus(order.id, value)}
                    >
                      <SelectTrigger className="h-auto w-auto border-0 p-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                        <Badge className={ORDER_STATUSES[order.status]?.color + " cursor-pointer"}>
                          {ORDER_STATUSES[order.status]?.label}
                          <ChevronDown className="h-3 w-3 ml-1" />
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ORDER_STATUSES).map(([key, { label, color }]) => (
                          <SelectItem key={key} value={key}>
                            <Badge className={color}>{label}</Badge>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {order.customer_id && customers[order.customer_id] && (
                    <div className="flex items-center justify-between">
                      <button
                        className="text-sm font-medium text-primary hover:underline underline-offset-2 truncate max-w-[60%] text-left"
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers?view=${order.customer_id}`); }}
                        title="Перейти к клиенту"
                      >
                        {customers[order.customer_id].name}
                      </button>
                      <CustomerSocialIcons customer={customers[order.customer_id]} />
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-border/50">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">Создан: {formatDateTime(order.created_at)}</span>
                      {(() => {
                        const cal = getCalendarDate(order);
                        return cal ? (
                          <span className="text-xs text-primary/80 flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {cal.label}: {cal.date}
                          </span>
                        ) : null;
                      })()}
                      <div>
                        <span className="font-bold text-base">{formatCurrency(order.total)}</span>
                        {order.prepayment > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Предоплата: {formatCurrency(order.prepayment)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 touch-manipulation"
                        onClick={() => setViewingOrder(order)}
                        title="Просмотреть заказ"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 touch-manipulation"
                        onClick={() => setEditingOrder(order)}
                        title="Редактировать заказ"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 touch-manipulation text-muted-foreground hover:text-foreground"
                        onClick={() => duplicateOrder(order)}
                        title="Дублировать заказ"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-destructive hover:bg-destructive/10 touch-manipulation"
                        onClick={() => deleteOrder(order.id)}
                        title="Удалить заказ"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: Card grid */}
      <div className="hidden md:block">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Нет заказов</p>
              <p className="text-muted-foreground mb-4">Создайте первый заказ</p>
              <Button onClick={() => setCreatingOrder(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Создать заказ
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-5 bg-card rounded-xl border shadow-sm hover:shadow-lg hover:border-primary/30 transition-all"
                  data-testid={`order-row-desktop-${order.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-primary">{order.order_number_display || `№${order.order_number}`}</span>
                      {order.form_schema_name && (
                        <Badge variant="outline" className="text-xs">
                          {order.form_schema_name}
                        </Badge>
                      )}
                    </div>
                    <Select 
                      value={order.status} 
                      onValueChange={(value) => updateOrderStatus(order.id, value)}
                    >
                        <SelectTrigger className="h-auto w-auto border-0 p-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                          <Badge className={ORDER_STATUSES[order.status]?.color + " cursor-pointer hover:opacity-80 transition-opacity"}>
                            {ORDER_STATUSES[order.status]?.label}
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ORDER_STATUSES).map(([key, { label, color }]) => (
                            <SelectItem key={key} value={key}>
                              <Badge className={color}>{label}</Badge>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                  </div>
                  
                  {order.customer_id && customers[order.customer_id] ? (
                    <div className="flex items-center justify-between mb-2">
                      <button
                        className="font-medium text-primary hover:underline underline-offset-2 truncate max-w-[65%] text-left"
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers?view=${order.customer_id}`); }}
                        title="Перейти к клиенту"
                      >
                        {customers[order.customer_id].name}
                      </button>
                      <CustomerSocialIcons customer={customers[order.customer_id]} />
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-sm mb-2">Без клиента</div>
                  )}
                  
                  <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
                    <div>Создан: {formatDateTime(order.created_at)}</div>
                    {(() => {
                      const cal = getCalendarDate(order);
                      return cal ? (
                        <div className="text-primary/80 flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {cal.label}: {cal.date}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border/50">
                    <div>
                      <div className="text-xs text-muted-foreground">Сумма</div>
                      <div className="font-bold text-lg">{formatCurrency(order.total)}</div>
                    </div>
                    {order.prepayment > 0 && (
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Предоплата</div>
                        <div className="font-semibold text-green-600">{formatCurrency(order.prepayment)}</div>
                      </div>
                    )}
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setViewingOrder(order)}
                        title="Просмотреть заказ"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditingOrder(order)}
                        title="Редактировать заказ"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => duplicateOrder(order)}
                        title="Дублировать заказ"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => deleteOrder(order.id)}
                        title="Удалить заказ"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Infinite scroll sentinel + counter */}
      <div ref={loadMoreRef} className="py-4 text-center">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Загрузка...</span>
          </div>
        )}
        {!loading && !loadingMore && sortedOrders.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Показано {sortedOrders.length} из {totalOrders}
            {!hasMore && totalOrders > 0 && ' — все заказы загружены'}
          </p>
        )}
      </div>
      {viewingOrder && (
        <OrderDetailSheet
          order={viewingOrder}
          customer={customers[viewingOrder.customer_id]}
          formSchema={formSchemas[viewingOrder.form_schema_id]}
          onClose={() => setViewingOrder(null)}
          onEdit={() => {
            const orderToEdit = viewingOrder;
            setViewingOrder(null);
            setEditingOrder(orderToEdit);
          }}
          onStatusChange={(newStatus) => {
            updateOrderStatus(viewingOrder.id, newStatus);
            // Don't update viewingOrder here for 'completed'/'cancelled' — dialog handlers will do it
            if (newStatus !== 'completed' && newStatus !== 'cancelled') {
              setViewingOrder(prev => prev ? { ...prev, status: newStatus } : prev);
            }
          }}
        />
      )}

      {/* Order Edit Sheet */}
      {editingOrder && (
        <OrderEditSheet
          order={editingOrder}
          customer={customers[editingOrder.customer_id]}
          formSchema={formSchemas[editingOrder.form_schema_id]}
          onClose={() => setEditingOrder(null)}
          onSaved={() => {
            setEditingOrder(null);
            loadOrders();
          }}
        />
      )}

      {/* Order Create Sheet */}
      {creatingOrder && (
        <OrderCreateSheet
          onClose={() => setCreatingOrder(false)}
          onCreated={() => {
            setCreatingOrder(false);
            loadOrders();
          }}
          setPaywallOpen={setPaywallOpen}
          setPaywallResource={setPaywallResource}
        />
      )}

      <PaywallDialog
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        resourceType={paywallResource}
        limit={3}
      />

      {/* Unpaid Invoices Dialog */}
      <Dialog open={unpaidDialogOpen} onOpenChange={(open) => { if (!open) handleUnpaidDialogClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-500" />
              Неоплаченные счета
            </DialogTitle>
            <DialogDescription>
              По этому заказу есть неоплаченные счета ({unpaidInvoices.length}). Выберите действие перед {pendingStatusChange?.newStatus === 'cancelled' ? 'отменой' : 'завершением'} заказа.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-2">
            {unpaidInvoices.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                <div className="min-w-0">
                  <span className="text-sm font-semibold">Счёт №{inv.invoice_number}</span>
                  {inv.customer_name && (
                    <p className="text-xs text-muted-foreground truncate">{inv.customer_name}</p>
                  )}
                </div>
                <span className="font-bold text-sm shrink-0 ml-3">{formatCurrency(inv.amount)}</span>
              </div>
            ))}
          </div>
          <div className="text-sm text-muted-foreground border-t pt-3">
            Итого неоплачено: <span className="font-bold text-foreground">{formatCurrency(unpaidInvoices.reduce((s, i) => s + (i.amount || 0), 0))}</span>
          </div>
          <DialogFooter className="flex-col gap-2">
            <Button onClick={handleUnpaidDialogMarkPaid} className="w-full">
              <Check className="h-4 w-4 mr-2" />
              Отметить как оплаченные
            </Button>
            <Button variant="destructive" onClick={handleUnpaidDialogDelete} className="w-full">
              <Trash2 className="h-4 w-4 mr-2" />
              Удалить счета
            </Button>
            <Button variant="outline" onClick={handleUnpaidDialogClose} className="w-full">
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Order Detail Sheet Component
function OrderDetailSheet({ order, customer, formSchema, onClose, onEdit, onStatusChange }) {
  const navigate = useNavigate();
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [orderInvoices, setOrderInvoices] = useState([]);

  useEffect(() => {
    if (order?.id) {
      invoicesAPI.getAll({ order_id: order.id }).then(res => {
        setOrderInvoices(res.data || []);
      }).catch(() => {});
    }
  }, [order?.id, invoiceDialogOpen]);
  
  if (!order) return null;

  const statusInfo = ORDER_STATUSES[order.status] || {};
  const remaining = (order.total || 0) - (order.prepayment || 0);

  // Get field labels from form schema
  const getFieldLabel = (fieldId) => {
    if (!formSchema?.fields) return fieldId;
    const field = formSchema.fields.find(f => f.id === fieldId);
    return field?.label || fieldId;
  };

  // Format field value for display
  const formatFieldValue = (fieldId, value) => {
    if (!formSchema?.fields) return String(value);
    const field = formSchema.fields.find(f => f.id === fieldId);
    
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
    if (field?.type === 'file') {
      if (Array.isArray(value)) return `${value.length} файл(ов)`;
      if (typeof value === 'object' && value.name) return value.name;
      if (typeof value === 'string') return value;
      return '—';
    }
    if (Array.isArray(value)) return value.join(', ');
    if (field?.type === 'date' && value) {
      try {
        return formatFormDate(value);
      } catch {
        return value;
      }
    }
    return String(value);
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-4 mt-2">
            <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center border-2 border-background shadow-sm">
              <ShoppingCart className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-lg font-bold">Заказ {order.order_number_display || `№${order.order_number}`}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDateTime(order.created_at)}
              </p>
              <Badge 
                variant={order.status === 'completed' ? 'default' : 'secondary'} 
                className="mt-2"
              >
                {statusInfo.label || order.status}
              </Badge>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-8">
          {/* Price Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {/* Total Amount */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-4">
              <Banknote className="absolute -right-2 -bottom-2 h-12 w-12 text-emerald-500/10" />
              <div className="relative">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Сумма</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(order.total || 0)}</p>
              </div>
            </div>
            
            {/* Prepayment */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 p-4">
              <CreditCard className="absolute -right-2 -bottom-2 h-12 w-12 text-blue-500/10" />
              <div className="relative">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                  Предоплата
                </p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(order.prepayment || 0)}
                </p>
              </div>
            </div>
            
            {/* Remaining - Full width on mobile, third column on desktop */}
            <div className="col-span-2 sm:col-span-1 relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 p-4">
              <Wallet className="absolute -right-2 -bottom-2 h-12 w-12 text-orange-500/10" />
              <div className="relative">
                <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">
                  К оплате
                </p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(remaining)}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice Button */}
          <Button 
            onClick={() => setInvoiceDialogOpen(true)} 
            className="w-full mt-4 h-11 font-medium"
            variant="outline"
          >
            <FileText className="h-4 w-4 mr-2" />
            Выставить счёт
          </Button>

          {/* Status Change */}
          <div className="mt-3">
            <Select value={order.status} onValueChange={(val) => onStatusChange(val)}>
              <SelectTrigger className="w-full h-11">
                <div className="flex items-center gap-2">
                  <div className={cn('h-2.5 w-2.5 rounded-full', ORDER_STATUSES[order.status]?.color)} />
                  <span className="text-xs text-muted-foreground mr-1">Статус:</span>
                  <span className="font-medium">{ORDER_STATUSES[order.status]?.label || order.status}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ORDER_STATUSES).map(([key, { label, color }]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className={cn('h-2.5 w-2.5 rounded-full', color)} />
                      {label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Customer Info */}
          {customer && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <User className="h-3.5 w-3.5" />
                Клиент
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    onClose();
                    navigate(`/customers?view=${customer.id}`);
                  }}
                >
                  <p className="font-medium text-primary">{customer.name} →</p>
                  <p className="text-xs text-muted-foreground mt-1">Нажмите для перехода к клиенту</p>
                </div>
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors" onClick={(e) => e.stopPropagation()}>
                    <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Phone className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm">{customer.phone}</span>
                  </a>
                )}
                {customer.email && (
                  <a href={`mailto:${customer.email}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors" onClick={(e) => e.stopPropagation()}>
                    <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm">{customer.email}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Order Data (Form Fields) */}
          {order.data && Object.keys(order.data).length > 0 && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <FileText className="h-3.5 w-3.5" />
                Данные заказа
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                {(formSchema?.fields || []).map((fieldDef) => {
                  // Render section as visual separator
                  if (fieldDef.type === 'section') {
                    const SIcon = fieldDef.icon ? SECTION_ICONS_MAP[fieldDef.icon] : null;
                    return (
                      <div key={fieldDef.id} className="px-4 pt-5 pb-2 bg-muted/50">
                        <div className="flex items-center gap-3">
                          {SIcon && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <SIcon className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <h3 className="text-sm font-semibold text-foreground">{fieldDef.label}</h3>
                          <div className="flex-1 border-t border-border" />
                        </div>
                        {fieldDef.description && (
                          <p className={`text-xs text-muted-foreground mt-1 ${SIcon ? 'ml-11' : ''}`}>{fieldDef.description}</p>
                        )}
                      </div>
                    );
                  }

                  const key = fieldDef.id;
                  const value = order.data[key];
                  // Skip empty values
                  if (value === null || value === undefined || value === '') return null;
                  const hasNotification = fieldDef.type === 'time' && (order.time_notifications || {})[key];
                  const isImageGallery = fieldDef.type === 'image_gallery';
                  const isFile = fieldDef.type === 'file';
                  return (
                    <div key={key} className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{fieldDef.label || getFieldLabel(key)}</p>
                      {isImageGallery ? (
                        <ImageGalleryReadonly images={value || []} apiUrl={process.env.REACT_APP_BACKEND_URL} />
                      ) : isFile ? (
                        <FileReadonly value={value} apiUrl={process.env.REACT_APP_BACKEND_URL} />
                      ) : (
                        <p className="font-medium">{formatFieldValue(key, value)}</p>
                      )}
                      {hasNotification && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <Bell className="h-3 w-3 text-primary" />
                          <span className="text-xs text-primary font-medium">Уведомление включено</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Render data fields not in schema (fallback for old orders) */}
                {Object.entries(order.data).filter(([key]) => 
                  !key.startsWith('_') && !formSchema?.fields?.find(f => f.id === key)
                ).map(([key, value]) => {
                  if (value === null || value === undefined || value === '') return null;
                  return (
                    <div key={key} className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{getFieldLabel(key)}</p>
                      <p className="font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form Name */}
          {formSchema && (
            <div className="mt-6 p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-xs text-muted-foreground">Форма заказа</p>
              <p className="text-sm font-medium">{formSchema.name}</p>
            </div>
          )}

          {/* Linked Invoices */}
          {orderInvoices.length > 0 && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <Receipt className="h-3.5 w-3.5" />
                Счета ({orderInvoices.length})
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                {orderInvoices.map(inv => {
                  const statusMap = {
                    new: { label: 'Ожидает', cls: 'text-blue-700 bg-blue-50' },
                    paid: { label: 'Оплачен', cls: 'text-emerald-700 bg-emerald-50' },
                    cancelled: { label: 'Отменён', cls: 'text-red-700 bg-red-50' },
                  };
                  const st = statusMap[inv.status] || statusMap.new;
                  return (
                    <div
                      key={inv.id}
                      className="p-4 flex items-center justify-between gap-3 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        onClose();
                        navigate(`/invoices?view=${inv.id}`);
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">№{inv.invoice_number}</span>
                          <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', st.cls)}>
                            {st.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatDateTime(inv.created_at)}
                          {inv.customer_name && ` · ${inv.customer_name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-bold text-sm">{formatCurrency(inv.amount)}</span>
                        <button
                          className="p-1.5 rounded-md hover:bg-background border border-transparent hover:border-border transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                            navigate(`/invoices?edit=${inv.id}`);
                          }}
                          title="Редактировать счёт"
                        >
                          <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        <button
                          className="p-1.5 rounded-md hover:bg-background border border-transparent hover:border-border transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `${window.location.origin}/invoice/${inv.public_id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Ссылка скопирована');
                          }}
                          title="Копировать ссылку для клиента"
                        >
                          <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        <a
                          href={`${window.location.origin}/invoice/${inv.public_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md hover:bg-background border border-transparent hover:border-border transition-colors"
                          onClick={(e) => e.stopPropagation()}
                          title="Открыть страницу оплаты"
                        >
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        </a>
                        <button
                          className="p-1.5 rounded-md hover:bg-background border border-transparent hover:border-border transition-colors"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('Удалить счёт №' + inv.invoice_number + '?')) return;
                            try {
                              await invoicesAPI.delete(inv.id);
                              toast.success('Счёт удалён');
                              setOrderInvoices(prev => prev.filter(i => i.id !== inv.id));
                            } catch { toast.error('Ошибка удаления'); }
                          }}
                          title="Удалить счёт"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8">
            <Button 
              onClick={onEdit} 
              className="w-full h-12 font-medium text-base"
              variant="default"
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать заказ
            </Button>
          </div>
        </div>

        {/* Invoice Dialog */}
        <CreateInvoiceDialog
          open={invoiceDialogOpen}
          onClose={() => setInvoiceDialogOpen(false)}
          onCreated={() => {
            setInvoiceDialogOpen(false);
          }}
          orderData={{
            order_id: order.id,
            order_number: order.order_number,
            total: order.total,
            prepayment: order.prepayment,
            customer_id: order.customer_id || null,
            customer_name: customer?.name || '',
            customer_phone: customer?.phone || '',
            customer_email: customer?.email || '',
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

// Order Edit Sheet Component
function OrderEditSheet({ order, customer, formSchema, onClose, onSaved }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(order.data || {});
  const [status, setStatus] = useState(order.status);
  const [prepayment, setPrepayment] = useState(order.prepayment || 0);
  const [total, setTotal] = useState(order.total || 0);
  const [saving, setSaving] = useState(false);
  const [timeNotifications, setTimeNotifications] = useState(order.time_notifications || {});
  const [unpaidDialogOpen, setUnpaidDialogOpen] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);

  const statusInfo = ORDER_STATUSES[status] || {};
  const remaining = total - prepayment;

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleTimeNotificationChange = (fieldId, enabled) => {
    setTimeNotifications(prev => ({ ...prev, [fieldId]: enabled }));
  };

  const handleSave = async () => {
    // If status changed to completed or cancelled, check for unpaid invoices
    if ((status === 'completed' || status === 'cancelled') && order.status !== status) {
      try {
        const res = await invoicesAPI.getAll({ order_id: order.id });
        const invoices = res.data || [];
        const unpaid = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
        if (unpaid.length > 0) {
          setUnpaidInvoices(unpaid);
          setUnpaidDialogOpen(true);
          return;
        }
      } catch (e) { /* proceed */ }
    }
    await executeSave();
  };

  const executeSave = async () => {
    setSaving(true);
    try {
      await ordersAPI.update(order.id, {
        data: formData,
        status,
        prepayment: Number(prepayment),
        total: Number(total),
        time_notifications: timeNotifications,
      });
      toast.success('Заказ обновлён');
      onSaved();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpaidMarkPaid = async () => {
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchPay(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) отмечены как оплаченные`);
    } catch (e) {
      toast.error('Ошибка при обновлении счетов');
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    await executeSave();
  };

  const handleUnpaidDelete = async () => {
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchDelete(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) удалены`);
    } catch (e) {
      toast.error('Ошибка при удалении счетов');
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    await executeSave();
  };

  const handleUnpaidClose = () => {
    // Close without changing anything
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
  };

  return (
    <>
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl p-0 overflow-y-auto [&>button]:hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent px-6 pt-8 pb-6 sticky top-0 z-10 backdrop-blur-sm border-b">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-4 mt-2">
            <div className="h-14 w-14 rounded-xl bg-blue-500/20 flex items-center justify-center border-2 border-background shadow-sm">
              <Edit className="h-7 w-7 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-lg font-bold">Редактирование заказа {order.order_number_display || `№${order.order_number}`}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDateTime(order.created_at)}
              </p>
            </div>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-180px)]">
          <div className="px-6 pt-6 pb-8 space-y-8">
            {/* Customer Info */}
            {customer && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                  <User className="h-3.5 w-3.5" />
                  Клиент
                </h4>
                <div
                  className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:bg-muted/50 transition-colors group"
                  onClick={() => { onClose(); navigate(`/customers?view=${customer.id}`); }}
                  title="Перейти к клиенту"
                >
                  <p className="font-medium text-primary group-hover:underline underline-offset-2">{customer.name} →</p>
                  {customer.phone && (
                    <p className="text-sm text-muted-foreground mt-1">{customer.phone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Status and Payment */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Статус и оплата
              </h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Статус</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Сумма заказа</Label>
                    <div className="relative">
                      <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={total || ''}
                        onChange={(e) => setTotal(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        className="pl-9"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Предоплата</Label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={prepayment || ''}
                        onChange={(e) => setPrepayment(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        className="pl-9"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Remaining amount */}
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">К оплате</span>
                    <span className="text-lg font-bold">{formatCurrency(remaining)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Data (Form Fields) */}
            {formSchema && formSchema.fields && (
              <div className="pt-2 pb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Данные заказа</h4>
                    <p className="text-xs text-muted-foreground">Информация из формы</p>
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
                  <DynamicForm
                    fields={formSchema.fields}
                    values={formData}
                    onChange={handleFieldChange}
                    timeNotifications={timeNotifications}
                    onTimeNotificationChange={handleTimeNotificationChange}
                  />
                </div>
              </div>
            )}

            {/* Form Name */}
            {formSchema && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground">Форма заказа</p>
                <p className="text-sm font-medium">{formSchema.name}</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with Actions */}
        <div className="px-6 py-4 border-t bg-background sticky bottom-0">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1"
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="flex-1"
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Unpaid Invoices Dialog */}
    <Dialog open={unpaidDialogOpen} onOpenChange={(open) => { if (!open) handleUnpaidClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-orange-500" />
            Неоплаченные счета
          </DialogTitle>
          <DialogDescription>
            По этому заказу есть неоплаченные счета ({unpaidInvoices.length}). Выберите действие перед {status === 'cancelled' ? 'отменой' : 'завершением'} заказа.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-60 overflow-y-auto py-2">
          {unpaidInvoices.map(inv => (
            <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
              <div className="min-w-0">
                <span className="text-sm font-semibold">Счёт №{inv.invoice_number}</span>
                {inv.customer_name && (
                  <p className="text-xs text-muted-foreground truncate">{inv.customer_name}</p>
                )}
              </div>
              <span className="font-bold text-sm shrink-0 ml-3">{formatCurrency(inv.amount)}</span>
            </div>
          ))}
        </div>
        <div className="text-sm text-muted-foreground border-t pt-3">
          Итого неоплачено: <span className="font-bold text-foreground">{formatCurrency(unpaidInvoices.reduce((s, i) => s + (i.amount || 0), 0))}</span>
        </div>
        <DialogFooter className="flex-col gap-2">
          <Button onClick={handleUnpaidMarkPaid} className="w-full">
            <Check className="h-4 w-4 mr-2" />
            Отметить как оплаченные
          </Button>
          <Button variant="destructive" onClick={handleUnpaidDelete} className="w-full">
            <Trash2 className="h-4 w-4 mr-2" />
            Удалить счета
          </Button>
          <Button variant="outline" onClick={handleUnpaidClose} className="w-full">
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// Order Create Sheet Component - Multi-step Wizard
function OrderCreateSheet({ onClose, onCreated, setPaywallOpen, setPaywallResource }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [schemas, setSchemas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [formData, setFormData] = useState({});
  const [timeNotifications, setTimeNotifications] = useState({});
  const [status, setStatus] = useState('new');
  const [prepayment, setPrepayment] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const totalSteps = 4;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [schemasRes, customersRes] = await Promise.all([
        formSchemaAPI.getAll(),
        customersAPI.getAll({ limit: 1000 }),
      ]);
      setSchemas(schemasRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleTimeNotificationChange = (fieldId, enabled) => {
    setTimeNotifications(prev => ({ ...prev, [fieldId]: enabled }));
  };
  const validateFormData = () => {
    if (!selectedSchema || !selectedSchema.fields) return true;
    
    const requiredFields = selectedSchema.fields.filter(field => field.required);
    const missingFields = [];
    
    for (const field of requiredFields) {
      const value = formData[field.id];
      if (value === undefined || value === null || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        missingFields.push(field.label || field.id);
      }
    }
    
    if (missingFields.length > 0) {
      toast.error(`Заполните обязательные поля: ${missingFields.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    // Validation for step 1
    if (currentStep === 1 && !selectedSchema) {
      toast.error('Выберите форму заказа');
      return;
    }
    
    // Validation for step 3 (form data)
    if (currentStep === 3) {
      if (!validateFormData()) {
        return;
      }
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Final validation before creating order
    if (!validateFormData()) {
      return;
    }
    
    setSaving(true);
    try {
      await ordersAPI.create({
        form_schema_id: selectedSchema.id,
        data: formData,
        customer_id: selectedCustomer || null,
        status,
        prepayment: Number(prepayment),
        total: Number(total),
        time_notifications: timeNotifications,
      });
      toast.success('Заказ создан');
      onCreated();
    } catch (error) {
      if (error.response?.status === 403) {
        setPaywallResource('orders');
        setPaywallOpen(true);
      } else {
        toast.error('Ошибка создания заказа');
      }
    } finally {
      setSaving(false);
    }
  };

  const remaining = total - prepayment;

  const steps = [
    { number: 1, title: 'Форма', icon: FileText },
    { number: 2, title: 'Клиент', icon: User },
    { number: 3, title: 'Данные', icon: Edit },
    { number: 4, title: 'Оплата', icon: Banknote },
  ];

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl p-0 overflow-hidden [&>button]:hidden flex flex-col">
        {/* Header with Progress */}
        <div className="bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent px-4 sm:px-6 pt-4 pb-3 border-b">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          
          <div className="mb-2">
            <h2 className="text-base font-bold leading-tight">Новый заказ</h2>
            <p className="text-xs text-muted-foreground">
              Шаг {currentStep} из {totalSteps}
            </p>
          </div>

          {/* Desktop Progress Steps */}
          <div className="hidden sm:flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center gap-0.5 flex-1">
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all",
                      isActive && "border-primary bg-primary text-primary-foreground shadow-lg scale-110",
                      isCompleted && "border-orange-500 bg-orange-500 text-white",
                      !isActive && !isCompleted && "border-border bg-muted text-muted-foreground"
                    )}>
                      {isCompleted ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Icon className="h-3.5 w-3.5" />
                      )}
                    </div>
                    <span className={cn(
                      "text-[11px] font-medium",
                      isActive && "text-primary",
                      isCompleted && "text-orange-600",
                      !isActive && !isCompleted && "text-muted-foreground"
                    )}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={cn(
                      "h-0.5 flex-1 mx-1.5 transition-all",
                      currentStep > step.number ? "bg-orange-500" : "bg-border"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile Progress Bar */}
          <div className="sm:hidden">
            <div className="flex items-center gap-2 mb-2">
              {steps.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.number;
                return (
                  <div
                    key={step.number}
                    className={cn(
                      "flex-1 h-1 rounded-full transition-all",
                      currentStep >= step.number ? "bg-primary" : "bg-border"
                    )}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {React.createElement(steps[currentStep - 1].icon, { 
                className: "h-4 w-4 text-primary" 
              })}
              <span className="text-sm font-medium">{steps[currentStep - 1].title}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 px-4 sm:px-6 py-6 space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-32" />
            <Skeleton className="h-20" />
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="px-4 sm:px-6 py-6">
              {/* Step 1: Schema Selection */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-in fade-in-50 slide-in-from-right-5 duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold leading-tight">Выберите форму заказа</h3>
                      <p className="text-xs text-muted-foreground">Форма определяет, какие данные нужно заполнить</p>
                    </div>
                  </div>

                  {schemas.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-8 text-center">
                      <FileText className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="font-medium mb-2">Нет созданных форм</p>
                      <p className="text-sm text-muted-foreground mb-5">
                        Сначала создайте форму в конструкторе, затем вернитесь сюда
                      </p>
                      <Link
                        to="/form-builder"
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        Перейти в конструктор форм
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {schemas.map((schema) => (
                        <div
                          key={schema.id}
                          onClick={() => setSelectedSchema(schema)}
                          className={cn(
                            "p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md",
                            selectedSchema?.id === schema.id
                              ? "border-primary bg-primary/5 shadow-sm"
                              : "border-border hover:border-primary/50"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                              selectedSchema?.id === schema.id
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            )}>
                              {selectedSchema?.id === schema.id ? (
                                <Check className="h-5 w-5" />
                              ) : (
                                <FileText className="h-5 w-5" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold mb-1">{schema.name}</h4>
                              {schema.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {schema.description}
                                </p>
                              )}
                              {schema.fields && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  {schema.fields.length} {schema.fields.length === 1 ? 'поле' : 'полей'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Customer Selection */}
              {currentStep === 2 && (
                <div className="space-y-4 animate-in fade-in-50 slide-in-from-right-5 duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold leading-tight">Выберите клиента</h3>
                      <p className="text-xs text-muted-foreground">Или создайте нового клиента</p>
                    </div>
                  </div>

                  <CustomerSelector 
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onSelect={setSelectedCustomer}
                    onCustomerCreated={(newCustomer) => {
                      setCustomers([...customers, newCustomer]);
                      setSelectedCustomer(newCustomer.id);
                    }}
                    setPaywallOpen={setPaywallOpen}
                    setPaywallResource={setPaywallResource}
                  />
                </div>
              )}

              {/* Step 3: Form Data */}
              {currentStep === 3 && selectedSchema && (
                <div className="space-y-4 animate-in fade-in-50 slide-in-from-right-5 duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Edit className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold leading-tight">{selectedSchema.name}</h3>
                      <p className="text-xs text-muted-foreground">Заполните данные заказа</p>
                    </div>
                  </div>

                  {selectedSchema.fields && selectedSchema.fields.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">Поля формы</span>
                      </div>
                      <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
                        <DynamicForm
                          fields={selectedSchema.fields}
                          values={formData}
                          onChange={handleFieldChange}
                          timeNotifications={timeNotifications}
                          onTimeNotificationChange={handleTimeNotificationChange}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Эта форма не содержит полей
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Payment */}
              {currentStep === 4 && (
                <div className="space-y-4 animate-in fade-in-50 slide-in-from-right-5 duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <Banknote className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold leading-tight">Оплата и статус</h3>
                      <p className="text-xs text-muted-foreground">Укажите финансовые детали заказа</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Статус заказа</Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="h-12">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Сумма заказа</Label>
                        <div className="relative">
                          <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="number"
                            value={total || ''}
                            onChange={(e) => setTotal(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            className="pl-9 h-12"
                            placeholder="0"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Предоплата</Label>
                        <div className="relative">
                          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="number"
                            value={prepayment || ''}
                            onChange={(e) => setPrepayment(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            className="pl-9 h-12"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Summary Card */}
                    <div className="rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-2 border-green-500/20 p-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Сумма заказа</span>
                          <span className="text-lg font-semibold">{formatCurrency(total)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Предоплата</span>
                          <span className="text-lg font-semibold">{formatCurrency(prepayment)}</span>
                        </div>
                        <div className="h-px bg-border" />
                        <div className="flex items-center justify-between">
                          <span className="font-medium">К оплате</span>
                          <span className="text-2xl font-bold text-primary">{formatCurrency(remaining)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Footer with Navigation */}
        <div className="px-4 sm:px-6 py-4 border-t bg-background">
          <div className="flex items-center gap-3">
            {currentStep > 1 ? (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="flex-1 h-11 touch-manipulation"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Назад
              </Button>
            ) : (
              <Button 
                variant="outline" 
                onClick={onClose}
                className="flex-1 h-11 touch-manipulation"
              >
                Отмена
              </Button>
            )}
            
            {currentStep < totalSteps ? (
              <Button 
                onClick={handleNext}
                disabled={(currentStep === 1 && !selectedSchema) || (currentStep === 2 && !selectedCustomer)}
                className="flex-1 h-11 touch-manipulation"
              >
                Далее
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={saving}
                className="flex-1 h-11 touch-manipulation"
              >
                {saving ? 'Создание...' : 'Создать заказ'}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Customer Selector Component with ability to create new customer inline
function CustomerSelector({ customers, selectedCustomer, onSelect, onCustomerCreated, setPaywallOpen, setPaywallResource }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSocial, setSelectedSocial] = useState('telegram');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSelectOpen, setCustomerSelectOpen] = useState(false);
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    social_links: {}
  });

  const selectedCustomerData = customers.find(c => c.id === selectedCustomer);

  const handleCustomerClick = () => {
    if (selectedCustomerData) {
      setCustomerDetailOpen(true);
    }
  };

  // Filter customers by search
  const filteredCustomers = customers.filter(customer => {
    if (!customerSearch) return true;
    const searchLower = customerSearch.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.phone?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  // Import social networks config
  const SOCIAL_OPTIONS = [
    { value: 'telegram', label: 'Telegram', placeholder: '@username' },
    { value: 'whatsapp', label: 'WhatsApp', placeholder: '+7XXXXXXXXXX' },
    { value: 'instagram', label: 'Instagram', placeholder: '@username' },
    { value: 'vk', label: 'ВКонтакте', placeholder: 'id или username' },
    { value: 'ok', label: 'Одноклассники', placeholder: 'ID профиля' },
    { value: 'vkmax', label: 'Max', placeholder: 'Ссылка max.ru/u/...' },
  ];

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Введите имя клиента');
      return;
    }

    setSaving(true);
    try {
      const socialLinks = Object.fromEntries(
        Object.entries(newCustomer.social_links).filter(([_, v]) => v && v.trim())
      );

      const res = await customersAPI.create({
        ...newCustomer,
        social_links: Object.keys(socialLinks).length > 0 ? socialLinks : null,
      });

      onCustomerCreated(res.data);
      setShowNewForm(false);
      setNewCustomer({ name: '', phone: '', email: '', social_links: {} });
      toast.success('Клиент создан');
    } catch (error) {
      if (error.response?.status === 403) {
        setPaywallResource('customers');
        setPaywallOpen(true);
      } else {
        toast.error('Ошибка создания клиента');
      }
    } finally {
      setSaving(false);
    }
  };

  const currentSocialOption = SOCIAL_OPTIONS.find(s => s.value === selectedSocial);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Клиент</CardTitle>
          {!showNewForm && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowNewForm(true)}
              className="h-8 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Новый
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showNewForm ? (
          <div className="space-y-3 p-3 border border-dashed border-border rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Новый клиент</span>
              <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)} className="h-7 w-7 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Input
              value={newCustomer.name}
              onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              placeholder="Имя *"
              className="h-9"
            />
            <Input
              value={newCustomer.phone}
              onChange={(e) => {
                let val = e.target.value.replace(/[^\d+]/g, '');
                const digits = val.replace(/\D/g, '');
                const isRussian = val.startsWith('+7') || val.startsWith('+8') || 
                                  (!val.startsWith('+') && (digits.startsWith('7') || digits.startsWith('8')));
                if (isRussian && digits.length > 0) {
                  const core = (digits.startsWith('7') || digits.startsWith('8')) ? digits.slice(1) : digits;
                  let formatted = '+7';
                  if (core.length > 0) formatted += ' (' + core.slice(0, 3);
                  if (core.length >= 3) formatted += ') ' + core.slice(3, 6);
                  if (core.length >= 6) formatted += '-' + core.slice(6, 8);
                  if (core.length >= 8) formatted += '-' + core.slice(8, 10);
                  val = formatted;
                }
                setNewCustomer({ ...newCustomer, phone: val });
              }}
              placeholder="+7 (999) 123-45-67"
              className="h-9"
            />
            
            {/* Social network selector */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Соцсеть для связи</Label>
              <div className="flex gap-2">
                <Select value={selectedSocial} onValueChange={setSelectedSocial}>
                  <SelectTrigger className="h-9 w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOCIAL_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newCustomer.social_links[selectedSocial] || ''}
                  onChange={(e) => setNewCustomer({ 
                    ...newCustomer, 
                    social_links: { ...newCustomer.social_links, [selectedSocial]: e.target.value }
                  })}
                  placeholder={currentSocialOption?.placeholder || ''}
                  className="h-9 flex-1"
                />
              </div>
            </div>

            <Button 
              size="sm" 
              onClick={handleCreateCustomer} 
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Создание...' : 'Создать клиента'}
            </Button>
          </div>
        ) : (
          <>
            {customerSelectOpen ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="p-3 border-b bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Выберите клиента</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => { setCustomerSelectOpen(false); setCustomerSearch(''); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Поиск по имени, телефону..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="customer-search-input"
                      autoFocus
                    />
                    {customerSearch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setCustomerSearch('')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className="max-h-[220px] overflow-y-auto"
                  onWheel={(e) => {
                    const el = e.currentTarget;
                    const hasScroll = el.scrollHeight > el.clientHeight;
                    if (!hasScroll) return;
                    const atTop = el.scrollTop === 0 && e.deltaY < 0;
                    const atBottom = Math.abs(el.scrollTop + el.clientHeight - el.scrollHeight) < 1 && e.deltaY > 0;
                    if (!atTop && !atBottom) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <div className="p-1.5">
                    <div
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                        !selectedCustomer && "bg-muted"
                      )}
                      onClick={() => {
                        onSelect('');
                        setCustomerSelectOpen(false);
                        setCustomerSearch('');
                      }}
                      data-testid="customer-option-none"
                    >
                      <Check className={cn("h-4 w-4", !selectedCustomer ? "opacity-100" : "opacity-0")} />
                      <span className="text-muted-foreground">Без клиента</span>
                    </div>
                    
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-6 text-center text-muted-foreground text-sm">
                        {customerSearch ? 'Клиенты не найдены' : 'Нет клиентов'}
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => {
                        const isSelected = selectedCustomer === customer.id;
                        return (
                          <div
                            key={customer.id}
                            className={cn(
                              "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                              isSelected && "bg-muted"
                            )}
                            onClick={() => {
                              onSelect(customer.id);
                              setCustomerSelectOpen(false);
                              setCustomerSearch('');
                            }}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{customer.name}</div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {customer.phone && <span>{customer.phone}</span>}
                                {customer.email && <span className="truncate">{customer.email}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                {customers.length > 0 && (
                  <div className="p-2 border-t text-xs text-muted-foreground text-center">
                    Найдено {filteredCustomers.length} из {customers.length}
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between h-10"
                data-testid="select-customer"
                onClick={() => setCustomerSelectOpen(true)}
              >
                {selectedCustomerData ? (
                  <span className="truncate">{selectedCustomerData.name}</span>
                ) : (
                  <span className="text-muted-foreground">Выберите клиента</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            )}

            {/* Show selected customer info */}
            {selectedCustomerData && (
              <CustomerMiniCardOrder customer={selectedCustomerData} onCustomerClick={handleCustomerClick} />
            )}

            {/* Customer detail dialog - opens instead of navigating away */}
            {selectedCustomerData && (
              <Dialog open={customerDetailOpen} onOpenChange={setCustomerDetailOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      {selectedCustomerData.name}
                    </DialogTitle>
                    <DialogDescription>Информация о клиенте</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 pt-2">
                    {selectedCustomerData.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        <a href={`tel:${selectedCustomerData.phone}`} className="hover:underline">{selectedCustomerData.phone}</a>
                      </div>
                    )}
                    {selectedCustomerData.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        <a href={`mailto:${selectedCustomerData.email}`} className="hover:underline">{selectedCustomerData.email}</a>
                      </div>
                    )}
                    {selectedCustomerData.address && (
                      <div className="flex items-center gap-3 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>{selectedCustomerData.address}</span>
                      </div>
                    )}
                    {selectedCustomerData.social_links && Object.keys(selectedCustomerData.social_links).length > 0 && (
                      <div className="pt-2 border-t">
                        <div className="text-xs text-muted-foreground mb-2">Соцсети</div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(selectedCustomerData.social_links).filter(([_, v]) => v).map(([key, value]) => {
                            const network = SOCIAL_NETWORKS[key];
                            if (!network) return null;
                            const link = network.getLink(value);
                            return (
                              <a
                                key={key}
                                href={link || '#'}
                                target={link ? "_blank" : undefined}
                                rel={link ? "noopener noreferrer" : undefined}
                                onClick={link ? undefined : (e) => e.preventDefault()}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-xs transition-colors"
                                style={{ color: network.color }}
                                title={`${network.name}: ${value}`}
                              >
                                <SocialIcon type={key} className="h-3.5 w-3.5" />
                                <span className="text-foreground">{value}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(selectedCustomerData.total_orders > 0 || selectedCustomerData.total_spent > 0) && (
                      <div className="pt-2 border-t">
                        <div className="text-xs text-muted-foreground mb-2">Статистика</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 rounded-lg bg-muted/50 text-center">
                            <div className="text-lg font-semibold">{selectedCustomerData.total_orders || 0}</div>
                            <div className="text-xs text-muted-foreground">Заказов</div>
                          </div>
                          <div className="p-2 rounded-lg bg-muted/50 text-center">
                            <div className="text-lg font-semibold">{(selectedCustomerData.total_spent || 0).toLocaleString('ru-RU')} ₽</div>
                            <div className="text-xs text-muted-foreground">Сумма</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedCustomerData.notes && (
                      <div className="pt-2 border-t">
                        <div className="text-xs text-muted-foreground mb-1">Заметки</div>
                        <p className="text-sm">{selectedCustomerData.notes}</p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCustomerDetailOpen(false)} className="w-full sm:w-auto">
                      Закрыть
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Customer card for order detail page
function OrderCustomerCard({ customer, onCustomerClick }) {
  const socialLinks = customer.social_links || {};

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button 
          onClick={onCustomerClick}
          className="font-semibold text-primary hover:underline text-left"
          title="Открыть карточку клиента"
        >
          {customer.name}
        </button>
      </div>
      
      {/* Contact info */}
      <div className="space-y-2">
        {customer.phone && (
          <a 
            href={`tel:${customer.phone}`} 
            className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
          >
            <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {customer.phone}
          </a>
        )}
        {customer.email && (
          <a 
            href={`mailto:${customer.email}`} 
            className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
          >
            <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {customer.email}
          </a>
        )}
        {customer.address && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {customer.address}
          </div>
        )}
      </div>

      {/* Social links */}
      {Object.values(socialLinks).some(v => v) && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          {Object.entries(socialLinks).filter(([_, v]) => v).map(([key, value]) => (
            <SocialLinkButton key={key} type={key} value={value} size="default" />
          ))}
        </div>
      )}

      {/* Stats */}
      {(customer.total_orders > 0 || customer.total_spent > 0) && (
        <div className="flex gap-4 pt-2 border-t border-border text-xs text-muted-foreground">
          <span>Заказов: {customer.total_orders || 0}</span>
          <span>Сумма: {formatCurrency(customer.total_spent || 0)}</span>
        </div>
      )}
    </div>
  );
}

// Mini card for order sidebar
function CustomerMiniCardOrder({ customer, onCustomerClick }) {
  const socialLinks = customer.social_links || {};
  const activeSocials = Object.entries(socialLinks).filter(([_, v]) => v);

  return (
    <div className="p-3 rounded-lg bg-muted/50 space-y-2">
      <div className="flex items-center justify-between">
        <button 
          onClick={onCustomerClick}
          className="font-medium text-sm text-primary hover:underline text-left"
          title="Посмотреть информацию о клиенте"
        >
          {customer.name}
        </button>
        {activeSocials.length > 0 && (
          <div className="flex gap-1">
            {activeSocials.map(([key, value]) => (
              <SocialLinkButton key={key} type={key} value={value} size="sm" />
            ))}
          </div>
        )}
      </div>
      {(customer.phone || customer.email) && (
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
          {customer.phone && (
            <a href={`tel:${customer.phone}`} className="hover:text-foreground">
              {customer.phone}
            </a>
          )}
          {customer.email && (
            <a href={`mailto:${customer.email}`} className="hover:text-foreground">
              {customer.email}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// New Order Page
export function NewOrderPage() {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [formData, setFormData] = useState({});
  const [timeNotifications, setTimeNotifications] = useState({});
  const [status, setStatus] = useState('new');
  const [prepayment, setPrepayment] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [schemasRes, customersRes] = await Promise.all([
        formSchemaAPI.getAll(),
        customersAPI.getAll({ limit: 1000 }),
      ]);
      setSchemas(schemasRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleTimeNotificationChange = (fieldId, enabled) => {
    setTimeNotifications(prev => ({ ...prev, [fieldId]: enabled }));
  };

  const handleSubmit = async () => {
    if (!selectedSchema) {
      toast.error('Выберите форму заказа');
      return;
    }

    setSaving(true);
    try {
      await ordersAPI.create({
        form_schema_id: selectedSchema.id,
        data: formData,
        customer_id: selectedCustomer || null,
        status,
        prepayment: Number(prepayment),
        total: Number(total),
        time_notifications: timeNotifications,
      });
      toast.success('Заказ создан');
      navigate('/orders');
    } catch (error) {
      toast.error('Ошибка создания заказа');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="new-order-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-black tracking-tight">Новый заказ</h1>
          <p className="text-muted-foreground mt-1">Заполните форму заказа</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Schema Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Форма заказа</CardTitle>
              <CardDescription>Выберите форму для создания заказа</CardDescription>
            </CardHeader>
            <CardContent>
              {schemas.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">Нет созданных форм</p>
                  <Link to="/form-builder">
                    <Button variant="outline">Создать форму</Button>
                  </Link>
                </div>
              ) : (
                <Select
                  value={selectedSchema?.id || ''}
                  onValueChange={(id) => {
                    const schema = schemas.find(s => s.id === id);
                    setSelectedSchema(schema);
                    setFormData({});
                  }}
                >
                  <SelectTrigger data-testid="select-schema">
                    <SelectValue placeholder="Выберите форму" />
                  </SelectTrigger>
                  <SelectContent>
                    {schemas.map((schema) => (
                      <SelectItem key={schema.id} value={schema.id}>
                        {schema.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          {/* Dynamic Form */}
          {selectedSchema && (
            <Card>
              <CardHeader>
                <CardTitle>{selectedSchema.name}</CardTitle>
                {selectedSchema.description && (
                  <CardDescription>{selectedSchema.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <DynamicForm
                  fields={selectedSchema.fields || []}
                  values={formData}
                  onChange={handleFieldChange}
                  timeNotifications={timeNotifications}
                  onTimeNotificationChange={handleTimeNotificationChange}
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Selection */}
          <CustomerSelector 
            customers={customers}
            selectedCustomer={selectedCustomer}
            onSelect={setSelectedCustomer}
            onCustomerCreated={(newCustomer) => {
              setCustomers([...customers, newCustomer]);
              setSelectedCustomer(newCustomer.id);
            }}
          />

          {/* Order Details */}
          <Card>
            <CardHeader>
              <CardTitle>Детали заказа</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Статус</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Сумма заказа</Label>
                <Input
                  type="number"
                  value={total || ''}
                  onChange={(e) => setTotal(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  placeholder="0"
                  data-testid="order-total-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Предоплата</Label>
                <Input
                  type="number"
                  value={prepayment || ''}
                  onChange={(e) => setPrepayment(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  placeholder="0"
                  data-testid="order-prepayment-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            className="w-full" 
            size="lg"
            onClick={handleSubmit}
            disabled={saving || !selectedSchema}
            data-testid="submit-order-btn"
          >
            {saving ? 'Сохранение...' : 'Создать заказ'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Order Detail Page
export function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [schema, setSchema] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [timeNotifications, setTimeNotifications] = useState({});
  const [status, setStatus] = useState('');
  const [prepayment, setPrepayment] = useState(0);
  const [total, setTotal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [unpaidDialogOpen, setUnpaidDialogOpen] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    try {
      const orderRes = await ordersAPI.getOne(id);
      setOrder(orderRes.data);
      setFormData(orderRes.data.data || {});
      setTimeNotifications(orderRes.data.time_notifications || {});
      setStatus(orderRes.data.status);
      setPrepayment(orderRes.data.prepayment);
      setTotal(orderRes.data.total);

      if (orderRes.data.form_schema_id) {
        const schemaRes = await formSchemaAPI.getOne(orderRes.data.form_schema_id);
        setSchema(schemaRes.data);
      }

      // Load customer data if customer_id exists
      if (orderRes.data.customer_id) {
        try {
          const customerRes = await customersAPI.getOne(orderRes.data.customer_id);
          setCustomer(customerRes.data);
        } catch (e) {
          console.error('Customer not found');
        }
      }
    } catch (error) {
      toast.error('Ошибка загрузки заказа');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleTimeNotificationChange = (fieldId, enabled) => {
    setTimeNotifications(prev => ({ ...prev, [fieldId]: enabled }));
  };

  const handleSave = async () => {
    // If status changed to completed or cancelled, check for unpaid invoices
    if ((status === 'completed' || status === 'cancelled') && order.status !== status) {
      try {
        const res = await invoicesAPI.getAll({ order_id: id });
        const invoices = res.data || [];
        const unpaid = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
        if (unpaid.length > 0) {
          setUnpaidInvoices(unpaid);
          setUnpaidDialogOpen(true);
          return; // Wait for dialog
        }
      } catch (e) { /* proceed */ }
    }
    await executeSave();
  };

  const executeSave = async () => {
    setSaving(true);
    try {
      await ordersAPI.update(id, {
        data: formData,
        status,
        prepayment: Number(prepayment),
        total: Number(total),
        time_notifications: timeNotifications,
      });
      toast.success('Заказ обновлён');
      setEditing(false);
      loadOrder();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpaidMarkPaid = async () => {
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchPay(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) отмечены как оплаченные`);
    } catch (e) {
      toast.error('Ошибка при обновлении счетов');
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    await executeSave();
  };

  const handleUnpaidDelete = async () => {
    try {
      const ids = unpaidInvoices.map(inv => inv.id);
      await invoicesAPI.batchDelete(ids);
      toast.success(`${unpaidInvoices.length} счёт(ов) удалены`);
    } catch (e) {
      toast.error('Ошибка при удалении счетов');
    }
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
    await executeSave();
  };

  const handleUnpaidClose = () => {
    // Close without changing anything
    setUnpaidDialogOpen(false);
    setUnpaidInvoices([]);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Удалить заказ ${order?.order_number_display || '№' + order?.order_number}?\n\nЭто действие нельзя отменить. Все данные заказа будут потеряны.`)) return;
    try {
      await ordersAPI.delete(id);
      toast.success('Заказ удалён');
      navigate('/orders');
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header - responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight truncate">
              Заказ {order.order_number_display || '#' + (order.order_number || order.id.slice(0, 8))}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatDateTime(order.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-11 sm:ml-0">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                Отмена
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-order-btn">
                {saving ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-order-btn">
                <Edit className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Редактировать</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setInvoiceDialogOpen(true)} data-testid="create-invoice-from-order-btn" className="text-primary border-primary/30 hover:bg-primary/5">
                <FileText className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Выставить счёт</span>
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-order-btn">
                <Trash2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Удалить</span>
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{schema?.name || 'Данные заказа'}</CardTitle>
            </CardHeader>
            <CardContent>
              {schema ? (
                editing ? (
                  <DynamicForm
                    fields={schema.fields || []}
                    values={formData}
                    onChange={handleFieldChange}
                    timeNotifications={timeNotifications}
                    onTimeNotificationChange={handleTimeNotificationChange}
                  />
                ) : (
                  <DynamicFormReadonly
                    fields={schema.fields || []}
                    values={formData}
                    timeNotifications={timeNotifications}
                  />
                )
              ) : (
                <pre className="bg-muted p-4 rounded-md text-sm overflow-auto">
                  {JSON.stringify(order.data, null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          {customer && (
            <Card data-testid="order-customer-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Клиент</CardTitle>
              </CardHeader>
              <CardContent>
                <OrderCustomerCard 
                  customer={customer} 
                  onCustomerClick={() => navigate(`/customers?highlight=${customer.id}`)} 
                />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Статус и оплата</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Статус</Label>
                {editing ? (
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={ORDER_STATUSES[order.status]?.color}>
                    {ORDER_STATUSES[order.status]?.label}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label>Сумма заказа</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={total || ''}
                    onChange={(e) => setTotal(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  />
                ) : (
                  <p className="text-2xl font-bold">{formatCurrency(order.total)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Предоплата</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={prepayment || ''}
                    onChange={(e) => setPrepayment(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  />
                ) : (
                  <p className="text-lg">{formatCurrency(order.prepayment)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Остаток</Label>
                <p className="text-lg text-muted-foreground">
                  {formatCurrency((editing ? total : order.total) - (editing ? prepayment : order.prepayment))}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Invoice Dialog */}
      <CreateInvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        onCreated={() => {
          setInvoiceDialogOpen(false);
        }}
        orderData={order ? {
          order_id: order.id,
          order_number: order.order_number,
          total: order.total,
          prepayment: order.prepayment,
          customer_id: order.customer_id || null,
          customer_name: customer?.name || '',
          customer_phone: customer?.phone || '',
          customer_email: customer?.email || '',
        } : null}
      />

      {/* Unpaid Invoices Dialog */}
      <Dialog open={unpaidDialogOpen} onOpenChange={(open) => { if (!open) handleUnpaidClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-500" />
              Неоплаченные счета
            </DialogTitle>
            <DialogDescription>
              По этому заказу есть неоплаченные счета ({unpaidInvoices.length}). Выберите действие перед {status === 'cancelled' ? 'отменой' : 'завершением'} заказа.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-2">
            {unpaidInvoices.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                <div className="min-w-0">
                  <span className="text-sm font-semibold">Счёт №{inv.invoice_number}</span>
                  {inv.customer_name && (
                    <p className="text-xs text-muted-foreground truncate">{inv.customer_name}</p>
                  )}
                </div>
                <span className="font-bold text-sm shrink-0 ml-3">{formatCurrency(inv.amount)}</span>
              </div>
            ))}
          </div>
          <div className="text-sm text-muted-foreground border-t pt-3">
            Итого неоплачено: <span className="font-bold text-foreground">{formatCurrency(unpaidInvoices.reduce((s, i) => s + (i.amount || 0), 0))}</span>
          </div>
          <DialogFooter className="flex-col gap-2">
            <Button onClick={handleUnpaidMarkPaid} className="w-full">
              <Check className="h-4 w-4 mr-2" />
              Отметить как оплаченные
            </Button>
            <Button variant="destructive" onClick={handleUnpaidDelete} className="w-full">
              <Trash2 className="h-4 w-4 mr-2" />
              Удалить счета
            </Button>
            <Button variant="outline" onClick={handleUnpaidClose} className="w-full">
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Image Gallery Field Component
function ImageGalleryField({ value, onChange, fieldId }) {
  const [uploading, setUploading] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const images = Array.isArray(value) ? value : [];
  const MAX_IMAGES = 4;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/svg+xml'];

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error(`Максимум ${MAX_IMAGES} фото`);
      return;
    }

    const filesToUpload = selected.slice(0, remaining);
    if (selected.length > remaining) {
      toast.warning(`Можно добавить ещё ${remaining}. Остальные пропущены`);
    }

    // Validate
    for (const file of filesToUpload) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: неподдерживаемый формат. Разрешены JPG, PNG, GIF, WebP`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: файл слишком большой (макс 10 МБ)`);
        return;
      }
    }

    setUploading(true);
    try {
      const newImages = [];
      const errors = [];
      for (const file of filesToUpload) {
        try {
          const res = await filesAPI.upload(file);
          newImages.push({
            id: res.data.file_id,
            url: res.data.file_path,
            thumb_url: res.data.thumb_path || res.data.file_path,
            name: res.data.file_name,
            description: ''
          });
        } catch (err) {
          errors.push(file.name);
        }
      }
      if (newImages.length > 0) {
        onChange([...images, ...newImages]);
        toast.success(`Загружено ${newImages.length} фото`);
      }
      if (errors.length > 0) {
        toast.error(`Не удалось загрузить: ${errors.join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки фото');
    } finally {
      setUploading(false);
    }
  };

  const updateDescription = (imageId, description) => {
    const updated = images.map(img => 
      img.id === imageId ? { ...img, description } : img
    );
    onChange(updated);
  };

  const removeImage = (imageId) => {
    onChange(images.filter(img => img.id !== imageId));
    filesAPI.delete(imageId).catch(() => {});
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <>
    <div className="space-y-4" data-testid={`field-${fieldId}`}>
      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image) => (
            <div key={image.id} className="space-y-2">
              <div className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                <img
                  src={`${API_URL}${image.thumb_url || image.url}`}
                  alt={image.name}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setViewingImage(`${API_URL}${image.url}`)}
                  loading="lazy"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={() => removeImage(image.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <Textarea
                value={image.description || ''}
                onChange={(e) => updateDescription(image.id, e.target.value)}
                placeholder="Описание фото..."
                rows={2}
                className="text-sm"
              />
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center gap-2">
        {images.length < MAX_IMAGES ? (
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              disabled={uploading}
            />
            <div className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg hover:border-primary transition-colors">
              {uploading ? (
                <>
                  <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Загрузка...</span>
                </>
              ) : (
                <>
                  <ImagePlus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Добавить фото</span>
                </>
              )}
            </div>
          </label>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {images.length}/{MAX_IMAGES} фото (макс 10 МБ)
        </span>
      </div>
    </div>

    {/* Image Viewer Dialog */}
    <Dialog open={!!viewingImage} onOpenChange={() => setViewingImage(null)}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden">
        <div className="relative bg-black rounded-lg">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => setViewingImage(null)}
          >
            <X className="h-5 w-5" />
          </Button>
          {viewingImage && (
            <img
              src={viewingImage}
              alt="Просмотр"
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

// File Upload Field Component
function FileUploadField({ value, onChange, fieldId }) {
  const [uploading, setUploading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const MAX_FILES = 3;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv', '.rtf', '.zip', '.rar', '.7z'];

  // Normalize value to array: handle null, string (old), single object (old), array (new)
  const files = Array.isArray(value) ? value
    : (value && typeof value === 'object' && value.url) ? [value]
    : [];

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      toast.error(`Максимум ${MAX_FILES} файлов`);
      return;
    }

    const filesToUpload = selected.slice(0, remaining);
    if (selected.length > remaining) {
      toast.warning(`Можно добавить ещё ${remaining}. Остальные пропущены`);
    }

    // Validate
    for (const file of filesToUpload) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) {
        toast.error(`${file.name}: неподдерживаемый формат. Разрешены: PDF, DOC, XLS, TXT, CSV, ZIP, RAR и изображения`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: файл слишком большой (макс 10 МБ)`);
        return;
      }
    }

    setUploading(true);
    try {
      const newFiles = [];
      const errors = [];
      for (const file of filesToUpload) {
        try {
          const res = await filesAPI.upload(file);
          newFiles.push({
            id: res.data.file_id,
            url: res.data.file_path,
            thumb_url: res.data.thumb_path || null,
            name: res.data.file_name,
          });
        } catch (err) {
          errors.push(file.name);
        }
      }
      if (newFiles.length > 0) {
        onChange([...files, ...newFiles]);
        toast.success(`Загружено ${newFiles.length} файл(ов)`);
      }
      if (errors.length > 0) {
        toast.error(`Не удалось загрузить: ${errors.join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    onChange(files.filter(f => f.id !== fileId));
    filesAPI.delete(fileId).catch(() => {});
  };

  return (
    <div className="space-y-3" data-testid={`field-${fieldId}`}>
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((fileData) => (
            <div key={fileData.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Paperclip className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={`${API_URL}${fileData.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline underline-offset-2 truncate block"
                >
                  {fileData.name}
                </a>
                <p className="text-xs text-muted-foreground mt-0.5">Загружен</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                onClick={() => removeFile(fileData.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {typeof value === 'string' && value ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
            <Paperclip className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Старый формат — загрузите заново</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
            onClick={() => onChange([])}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : null}

      {files.length < MAX_FILES ? (
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
          <div className="flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg hover:border-primary transition-colors">
            {uploading ? (
              <>
                <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Загрузка...</span>
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {files.length > 0 ? 'Добавить ещё файлы' : 'Загрузить файлы'}
                </span>
              </>
            )}
          </div>
        </label>
      ) : null}
      <p className="text-xs text-muted-foreground">{files.length}/{MAX_FILES} файлов (макс 10 МБ · PDF, DOC, XLS, ZIP и др.)</p>
    </div>
  );
}

// Dynamic Form Component
function DynamicForm({ fields, values, onChange, timeNotifications, onTimeNotificationChange }) {
  const [reminderMinutes, setReminderMinutes] = useState(30);

  useEffect(() => {
    settingsAPI.getNotifications().then(res => {
      if (res.data.reminder_minutes) {
        setReminderMinutes(res.data.reminder_minutes);
      }
    }).catch(() => {});
  }, []);

  const shouldShowField = useCallback((field, allValues, allFields, visited) => {
    if (!field.conditional) return true;
    
    const { field_id, operator, value } = field.conditional;

    // "is_visible" / "is_not_visible" — check if the referenced field is currently shown
    if (operator === 'is_visible' || operator === 'is_not_visible') {
      const targetField = allFields?.find(f => f.id === field_id);
      if (!targetField) return true;
      // Protect against circular dependency
      const visitedSet = visited || new Set();
      if (visitedSet.has(field.id)) return true;
      visitedSet.add(field.id);
      const targetVisible = shouldShowField(targetField, allValues, allFields, visitedSet);
      return operator === 'is_visible' ? targetVisible : !targetVisible;
    }

    const fieldValue = allValues[field_id];
    
    switch (operator) {
      case 'equals': return fieldValue === value;
      case 'not_equals': return fieldValue !== value;
      case 'contains': return String(fieldValue).includes(value);
      case 'greater_than': return Number(fieldValue) > Number(value);
      case 'less_than': return Number(fieldValue) < Number(value);
      default: return true;
    }
  }, []);

  return (
    <div>
      {fields.map((field) => {
        if (!shouldShowField(field, values, fields)) return null;

        // Section is a visual separator only
        if (field.type === 'section') {
          const SIcon = field.icon ? SECTION_ICONS_MAP[field.icon] : null;
          return (
            <div key={field.id} className="pt-6 pb-2 px-4 -mx-4 bg-muted/50">
              <div className="flex items-center gap-3">
                {SIcon && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <SIcon className="h-4 w-4 text-primary" />
                  </div>
                )}
                <h3 className="text-base font-semibold text-foreground">{field.label}</h3>
                <div className="flex-1 border-t border-border" />
              </div>
              {field.description && (
                <p className={`text-xs text-muted-foreground mt-1.5 ${SIcon ? 'ml-11' : ''}`}>{field.description}</p>
              )}
            </div>
          );
        }
        
        return (
          <DynamicField
            key={field.id}
            field={field}
            value={values[field.id]}
            onChange={(val) => onChange(field.id, val)}
            notificationEnabled={timeNotifications ? timeNotifications[field.id] || false : false}
            onNotificationChange={onTimeNotificationChange ? (val) => onTimeNotificationChange(field.id, val) : undefined}
            reminderMinutes={reminderMinutes}
          />
        );
      })}
    </div>
  );
}

function DynamicField({ field, value, onChange, notificationEnabled, onNotificationChange, reminderMinutes }) {
  const renderInput = () => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder}
            min={field.min_value}
            max={field.max_value}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger data-testid={`field-${field.id}`}>
              <SelectValue placeholder={field.placeholder || 'Выберите...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt.id} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2" data-testid={`field-${field.id}`}>
            {(field.options || []).map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter(v => v !== opt.value);
                    onChange(newValues);
                  }}
                  id={`field-${field.id}-option-${opt.id}`}
                />
                <label htmlFor={`field-${field.id}-option-${opt.id}`} className="text-sm cursor-pointer">
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={value || false}
              onCheckedChange={onChange}
              id={`field-${field.id}`}
              data-testid={`field-${field.id}`}
            />
            <label htmlFor={`field-${field.id}`} className="text-sm">
              {field.placeholder || field.label}
            </label>
          </div>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={`${field.id}-${opt.id}`}
                  name={field.id}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                  className="h-4 w-4"
                />
                <label htmlFor={`${field.id}-${opt.id}`} className="text-sm">
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'date': {
        // Parse date value correctly for both YYYY-MM-DD and ISO formats
        const parseDate = (val) => {
          if (!val) return undefined;
          const str = String(val);
          // YYYY-MM-DD format — parse as local date (not UTC)
          if (str.length === 10 && str.includes('-')) {
            const [y, m, d] = str.split('-').map(Number);
            return new Date(y, m - 1, d);
          }
          // Old ISO format — handle timezone shift
          if (str.includes('T')) {
            const dt = new Date(str);
            if (dt.getUTCHours() >= 12) {
              const nextDay = new Date(dt);
              nextDay.setUTCDate(nextDay.getUTCDate() + 1);
              return new Date(nextDay.getUTCFullYear(), nextDay.getUTCMonth(), nextDay.getUTCDate());
            }
            return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
          }
          return new Date(str);
        };
        // Format date for storage as YYYY-MM-DD (timezone-safe)
        const formatLocalDate = (date) => {
          if (!date) return null;
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        };
        const parsedDate = parseDate(value);
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
                data-testid={`field-${field.id}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {parsedDate ? format(parsedDate, 'PPP', { locale: ru }) : 'Выберите дату'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={parsedDate}
                onSelect={(date) => onChange(formatLocalDate(date))}
                locale={ru}
              />
            </PopoverContent>
          </Popover>
        );
      }
      
      case 'time':
        return (
          <div className="space-y-4">
            <Input
              type="time"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full max-w-full box-border"
              style={{ maxWidth: '100%', minWidth: 0 }}
              data-testid={`field-${field.id}`}
            />
            {onNotificationChange && (
              <label className="flex items-center gap-2.5 cursor-pointer select-none group mt-1">
                <div
                  role="switch"
                  aria-checked={notificationEnabled}
                  tabIndex={0}
                  onClick={() => onNotificationChange(!notificationEnabled)}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onNotificationChange(!notificationEnabled); } }}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    notificationEnabled ? "bg-primary" : "bg-muted-foreground/30"
                  )}
                  data-testid={`notification-toggle-${field.id}`}
                >
                  <span
                    className={cn(
                      "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out",
                      notificationEnabled ? "translate-x-4" : "translate-x-0"
                    )}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Bell className={cn("h-3.5 w-3.5", notificationEnabled ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("text-xs", notificationEnabled ? "text-foreground font-medium" : "text-muted-foreground")}>
                    Уведомление
                  </span>
                </div>
              </label>
            )}
            {notificationEnabled && value && reminderMinutes && (
              <p className="text-xs text-muted-foreground mt-1">
                Уведомление за {reminderMinutes >= 60 ? `${Math.floor(reminderMinutes / 60)} ч${reminderMinutes % 60 > 0 ? ` ${reminderMinutes % 60} мин` : ''}` : `${reminderMinutes} мин`} — в {(() => {
                  const [h, m] = value.split(':').map(Number);
                  const total = h * 60 + m - reminderMinutes;
                  const rh = Math.floor(((total % 1440) + 1440) % 1440 / 60);
                  const rm = ((total % 1440) + 1440) % 1440 % 60;
                  return `${String(rh).padStart(2,'0')}:${String(rm).padStart(2,'0')}`;
                })()}
              </p>
            )}
          </div>
        );
      
      case 'file':
        return (
          <FileUploadField
            value={value}
            onChange={onChange}
            fieldId={field.id}
          />
        );
      
      case 'image_gallery':
        return (
          <ImageGalleryField
            value={value || []}
            onChange={onChange}
            fieldId={field.id}
          />
        );
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <div className="space-y-3 mb-5">
      <Label className="flex items-center gap-1 text-sm font-semibold text-foreground">
        {field.label}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
      {renderInput()}
    </div>
  );
}

function DynamicFormReadonly({ fields, values, timeNotifications }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [reminderMinutes, setReminderMinutes] = useState(30);

  useEffect(() => {
    settingsAPI.getNotifications().then(res => {
      if (res.data.reminder_minutes) {
        setReminderMinutes(res.data.reminder_minutes);
      }
    }).catch(() => {});
  }, []);
  
  return (
    <div className="space-y-4">
      {fields.map((field) => {
        // Section is a visual separator
        if (field.type === 'section') {
          const SIcon = field.icon ? SECTION_ICONS_MAP[field.icon] : null;
          return (
            <div key={field.id} className="pt-4 pb-1 px-4 -mx-4 bg-muted/50">
              <div className="flex items-center gap-3">
                {SIcon && (
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <SIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                )}
                <h3 className="text-sm font-semibold text-foreground">{field.label}</h3>
                <div className="flex-1 border-t border-border" />
              </div>
              {field.description && (
                <p className={`text-xs text-muted-foreground mt-1 ${SIcon ? 'ml-10' : ''}`}>{field.description}</p>
              )}
            </div>
          );
        }

        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-muted-foreground">{field.label}</Label>
            {field.type === 'image_gallery' ? (
              <ImageGalleryReadonly images={values[field.id] || []} apiUrl={API_URL} />
            ) : field.type === 'file' ? (
              <FileReadonly value={values[field.id]} apiUrl={API_URL} />
            ) : (
              <>
                <p className="text-sm">{formatFieldValue(field, values[field.id])}</p>
                {field.type === 'time' && timeNotifications && timeNotifications[field.id] && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Bell className="h-3 w-3 text-primary" />
                    <span className="text-xs text-primary font-medium">
                      Уведомление{values[field.id] ? (() => {
                        const [h, m] = values[field.id].split(':').map(Number);
                        if (isNaN(h) || isNaN(m)) return ' включено';
                        const total = h * 60 + m - reminderMinutes;
                        const rh = Math.floor(((total % 1440) + 1440) % 1440 / 60);
                        const rm = ((total % 1440) + 1440) % 1440 % 60;
                        const rmLabel = reminderMinutes >= 60 ? `${Math.floor(reminderMinutes / 60)} ч${reminderMinutes % 60 > 0 ? ` ${reminderMinutes % 60} мин` : ''}` : `${reminderMinutes} мин`;
                        return ` за ${rmLabel} — в ${String(rh).padStart(2,'0')}:${String(rm).padStart(2,'0')}`;
                      })() : ' включено'}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ImageGalleryReadonly({ images, apiUrl }) {
  const [viewingImage, setViewingImage] = useState(null);
  
  if (!images || images.length === 0) return <p className="text-sm">—</p>;
  
  return (
    <>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
      {images.map((image) => (
        <div key={image.id} className="space-y-2">
          <div className="aspect-square rounded-lg overflow-hidden border border-border bg-muted">
            <img
              src={`${apiUrl}${image.thumb_url || image.url}`}
              alt={image.name}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setViewingImage(`${apiUrl}${image.url}`)}
              loading="lazy"
            />
          </div>
          {image.description && (
            <p className="text-xs text-muted-foreground">{image.description}</p>
          )}
        </div>
      ))}
    </div>

    {/* Image Viewer Dialog */}
    <Dialog open={!!viewingImage} onOpenChange={() => setViewingImage(null)}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden">
        <div className="relative bg-black rounded-lg">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => setViewingImage(null)}
          >
            <X className="h-5 w-5" />
          </Button>
          {viewingImage && (
            <img
              src={viewingImage}
              alt="Просмотр"
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

function FileReadonly({ value, apiUrl }) {
  // Handle null/undefined
  if (!value) return <p className="text-sm">—</p>;

  // Normalize to array for rendering
  const files = Array.isArray(value) ? value
    : (typeof value === 'object' && value.url) ? [value]
    : [];

  // Old format: just a string filename
  if (files.length === 0 && typeof value === 'string' && value) {
    return (
      <div className="inline-flex items-center gap-2 p-2.5 rounded-lg border border-border bg-muted/20 mt-1">
        <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm">{value}</span>
      </div>
    );
  }

  if (files.length === 0) return <p className="text-sm">—</p>;

  return (
    <div className="space-y-2 mt-1.5">
      {files.map((file) => {
        const isImage = /\.(jpg|jpeg|png|gif|webp|svg|heic|heif)$/i.test(file.name || '');
        const fullUrl = `${apiUrl}${file.url}`;

        return isImage ? (
          <div key={file.id} className="space-y-2">
            <div className="w-40 h-40 rounded-lg overflow-hidden border border-border bg-muted">
              <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={fullUrl}
                  alt={file.name}
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                />
              </a>
            </div>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline underline-offset-2"
            >
              <Download className="h-3 w-3" />
              {file.name}
            </a>
          </div>
        ) : (
          <a
            key={file.id}
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 transition-colors group max-w-full"
          >
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Paperclip className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium truncate group-hover:text-primary transition-colors">
              {file.name}
            </span>
            <Download className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-auto" />
          </a>
        );
      })}
    </div>
  );
}

function formatFieldValue(field, value) {
  if (value === undefined || value === null || value === '') return '—';
  
  switch (field.type) {
    case 'checkbox':
      return value ? 'Да' : 'Нет';
    case 'date':
      return formatFormDate(value);
    case 'select':
    case 'radio':
      const option = field.options?.find(o => o.value === value);
      return option?.label || value;
    case 'multi_select':
      if (!Array.isArray(value) || value.length === 0) return '—';
      const selectedOptions = value
        .map(v => field.options?.find(o => o.value === v)?.label || v)
        .filter(Boolean);
      return selectedOptions.join(', ');
    case 'image_gallery':
      if (Array.isArray(value)) return `${value.length} фото`;
      return '—';
    case 'file':
      if (Array.isArray(value)) return `${value.length} файл(ов)`;
      if (typeof value === 'object' && value.name) return value.name;
      if (typeof value === 'string') return value;
      return '—';
    default:
      return String(value);
  }
}
